import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut,
  MapPin,
  Users,
  Tag,
  Clock,
  Check,
  X,
  Sparkles,
  RefreshCw,
  ChevronDown,
  AlertCircle,
  Calendar,
  Download,
  FileJson,
  FileText,
  Share2,
  Copy,
  Settings,
  ToggleLeft,
  ToggleRight,
  CreditCard,
  Lock,
  Palette,
  Plus,
  Trash2,
  Edit3,
  Save,
  Database,
  Type,
  Image,
  Mail,
  Cookie,
  BarChart3,
  Globe,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "../theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color options for new labels
const COLOR_OPTIONS = [
  "#F59E0B", "#10B981", "#8B5CF6", "#EF4444", "#3B82F6",
  "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16",
  "#06B6D4", "#A855F7", "#22C55E", "#E11D48",
];

export default function AdminDashboard() {
  const { isDark } = useTheme();
  const [stats, setStats] = useState(null);
  const [labelRequests, setLabelRequests] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState({});
  const [expandedArea, setExpandedArea] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportData, setExportData] = useState(null);
  // Settings state
  const [settings, setSettings] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState("#6366F1");
  const [editingLabel, setEditingLabel] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [appNameEdit, setAppNameEdit] = useState("");
  const [heroTaglineEdit, setHeroTaglineEdit] = useState("");
  const [labelRequestTextEdit, setLabelRequestTextEdit] = useState("");
  const [privacyPolicyEdit, setPrivacyPolicyEdit] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  // Subscriber & Cookie Analytics
  const [subscribers, setSubscribers] = useState({ subscribers: [], total: 0 });
  const [cookieAnalytics, setCookieAnalytics] = useState(null);
  // Marketing settings
  const [marketingSettings, setMarketingSettings] = useState({ klaviyo_api_key: "", klaviyo_list_id: "", meta_pixel_id: "", tiktok_pixel_id: "" });
  const [stripeKeyEdit, setStripeKeyEdit] = useState("");
  // Social links
  const [socialEdit, setSocialEdit] = useState({ show_social_links: false, social_tiktok_url: "", social_instagram_url: "", social_youtube_url: "" });
  // Event action modal state
  const [eventActionModal, setEventActionModal] = useState({ show: false, eventId: null, action: "", eventTitle: "", reason: "" });
  // Pin lookup & remove
  const [pinSearch, setPinSearch] = useState("");
  const [pinResults, setPinResults] = useState([]);
  const [pinSearching, setPinSearching] = useState(false);
  // Login attempts audit
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [loginAttemptsLoading, setLoginAttemptsLoading] = useState(false);
  // Login alerts & IP allowlist
  const [securityEdit, setSecurityEdit] = useState({ admin_alert_email: "", admin_allowed_ips: "" });
  const [myIp, setMyIp] = useState("");
  // Backups
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  // Klaviyo sync status
  const [klaviyoStatus, setKlaviyoStatus] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("neuromap_token");

  useEffect(() => {
    if (!token) {
      navigate("/portal-d7glqibsq4");
      return;
    }
    fetchData();
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;
    if (activeTab === "settings") {
      fetchKlaviyoStatus();
    }
    if (activeTab === "overview") {
      fetchLoginAttempts();
    }
  }, [activeTab, token]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, requestsRes, eventsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/label-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setLabelRequests(requestsRes.data);
      setEvents(eventsRes.data);
      setSettings(settingsRes.data);
      setAppNameEdit(settingsRes.data?.app_name || "Labelled");
      setHeroTaglineEdit(settingsRes.data?.hero_tagline || "");
      setLabelRequestTextEdit(settingsRes.data?.label_request_text || "");
      setPrivacyPolicyEdit(settingsRes.data?.privacy_policy_content || "");
      setMarketingSettings({
        klaviyo_api_key: settingsRes.data?.klaviyo_api_key || "",
        klaviyo_list_id: settingsRes.data?.klaviyo_list_id || "",
        meta_pixel_id: settingsRes.data?.meta_pixel_id || "",
        tiktok_pixel_id: settingsRes.data?.tiktok_pixel_id || "",
      });
      setStripeKeyEdit(settingsRes.data?.stripe_api_key || "");
      setSocialEdit({
        show_social_links: settingsRes.data?.show_social_links === true,
        social_tiktok_url: settingsRes.data?.social_tiktok_url || "",
        social_instagram_url: settingsRes.data?.social_instagram_url || "",
        social_youtube_url: settingsRes.data?.social_youtube_url || "",
      });
      setSecurityEdit({
        admin_alert_email: settingsRes.data?.admin_alert_email || "",
        admin_allowed_ips: settingsRes.data?.admin_allowed_ips || "",
      });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("neuromap_token");
        navigate("/portal-d7glqibsq4");
      } else {
        toast.error("Failed to load data");
      }
    }
    setIsLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("neuromap_token");
    navigate("/admin");
  };

  const handleRequestAction = async (requestId, action) => {
    try {
      const color = action === "approve" ? selectedColor[requestId] || "#6B7280" : undefined;
      await axios.put(
        `${API}/admin/label-requests/${requestId}?action=${action}${color ? `&color=${encodeURIComponent(color)}` : ""}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Request ${action}d successfully`);
      fetchData();
    } catch (err) {
      toast.error(`Failed to ${action} request`);
    }
  };

  const handleEventAction = async (eventId, action, reason = "") => {
    try {
      await axios.put(
        `${API}/admin/events/${eventId}?action=${action}`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Event ${action}d successfully`);
      setEventActionModal({ show: false, eventId: null, action: "", eventTitle: "", reason: "" });
      fetchData();
    } catch (err) {
      toast.error(`Failed to ${action} event`);
    }
  };

  const handleDeleteEvent = async (eventId, reason = "") => {
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { reason },
      });
      toast.success("Event deleted successfully");
      setEventActionModal({ show: false, eventId: null, action: "", eventTitle: "", reason: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to delete event");
    }
  };

  const openEventActionModal = (eventId, action, eventTitle) => {
    setEventActionModal({ show: true, eventId, action, eventTitle, reason: "" });
  };

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API}/admin/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExportData(res.data);
      
      if (format === "csv") {
        // Download CSV
        const blob = new Blob([res.data.csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("CSV downloaded!");
      } else {
        // Show JSON in modal
        setShowExportModal(true);
      }
    } catch (err) {
      toast.error("Failed to export data");
    }
  };

  const copyExportData = () => {
    navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
    toast.success("Copied to clipboard!");
  };

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "neuromap_export.json";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("JSON downloaded!");
  };

  const shareAreaLink = (area) => {
    const url = `${window.location.origin}?town=${encodeURIComponent(area.town)}&country=${encodeURIComponent(area.country)}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied!");
  };

  // Settings handlers
  const toggleSetting = async (key) => {
    try {
      const newVal = !settings[key];
      await axios.put(`${API}/admin/settings`, { [key]: newVal }, { headers: { Authorization: `Bearer ${token}` } });
      setSettings({ ...settings, [key]: newVal });
      toast.success(`${key.replace(/_/g, " ")} ${newVal ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to update setting"); }
  };

  const saveAppName = async () => {
    if (!appNameEdit.trim()) return;
    try {
      await axios.put(`${API}/admin/settings`, { app_name: appNameEdit.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setSettings({ ...settings, app_name: appNameEdit.trim() });
      toast.success("App name updated!");
    } catch { toast.error("Failed to update app name"); }
  };

  const saveHeroTagline = async () => {
    try {
      await axios.put(`${API}/admin/settings`, { hero_tagline: heroTaglineEdit.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setSettings({ ...settings, hero_tagline: heroTaglineEdit.trim() });
      toast.success("Tagline updated!");
    } catch { toast.error("Failed to update tagline"); }
  };

  const saveLabelRequestText = async () => {
    try {
      await axios.put(`${API}/admin/settings`, { label_request_text: labelRequestTextEdit.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setSettings({ ...settings, label_request_text: labelRequestTextEdit.trim() });
      toast.success("Label request text updated!");
    } catch { toast.error("Failed to update"); }
  };

  const savePrivacyPolicy = async () => {
    try {
      await axios.put(`${API}/admin/settings`, { privacy_policy_content: privacyPolicyEdit }, { headers: { Authorization: `Bearer ${token}` } });
      setSettings({ ...settings, privacy_policy_content: privacyPolicyEdit });
      toast.success("Privacy policy updated!");
    } catch { toast.error("Failed to update privacy policy"); }
  };

  const saveSocialLinks = async () => {
    try {
      const payload = {
        show_social_links: !!socialEdit.show_social_links,
        social_tiktok_url: socialEdit.social_tiktok_url.trim(),
        social_instagram_url: socialEdit.social_instagram_url.trim(),
        social_youtube_url: socialEdit.social_youtube_url.trim(),
      };
      await axios.put(`${API}/admin/settings`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setSettings({ ...settings, ...payload });
      toast.success("Social links updated!");
    } catch { toast.error("Failed to update social links"); }
  };

  const handleAddLabel = async () => {
    if (!newLabelName.trim()) { toast.error("Enter a label name"); return; }
    try {
      await axios.post(`${API}/admin/labels`, { name: newLabelName.trim(), color: newLabelColor }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Label added!");
      setNewLabelName(""); setNewLabelColor("#6366F1");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add label"); }
  };

  const handleDeleteLabel = async (labelId) => {
    if (!window.confirm("Delete this label? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/labels/${labelId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Label deleted");
      fetchData();
    } catch { toast.error("Failed to delete label"); }
  };

  const handleUpdateLabel = async (labelId, data) => {
    try {
      await axios.put(`${API}/admin/labels/${labelId}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Label updated");
      setEditingLabel(null);
      fetchData();
    } catch { toast.error("Failed to update label"); }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm) { toast.error("Passwords don't match"); return; }
    if (passwordForm.new_password.length < 12) { toast.error("Password must be at least 12 characters"); return; }
    if (!/[A-Z]/.test(passwordForm.new_password) || !/[a-z]/.test(passwordForm.new_password) || !/[0-9]/.test(passwordForm.new_password)) {
      toast.error("Password must include uppercase, lowercase, and a number");
      return;
    }
    try {
      await axios.put(`${API}/admin/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password changed!");
      setPasswordForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to change password"); }
  };

  const fetchStripeStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe-status`, { headers: { Authorization: `Bearer ${token}` } });
      setStripeStatus(res.data);
    } catch { toast.error("Failed to check Stripe status"); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("File too large (max 2MB)"); return; }
    
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/admin/logo`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Logo uploaded!");
      // Force reload logo preview
      const logoPreview = document.getElementById("logo-preview");
      if (logoPreview) logoPreview.src = `${API}/logo?t=${Date.now()}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload logo");
    }
    setLogoUploading(false);
  };

  const getLabelName = (labelId) => {
    const label = stats?.labels?.find((l) => l.id === labelId);
    return label?.name || labelId;
  };

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/subscribers`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribers(res.data);
    } catch { toast.error("Failed to load subscribers"); }
  };

  const fetchCookieAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/cookie-analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setCookieAnalytics(res.data);
    } catch { toast.error("Failed to load cookie analytics"); }
  };

  const saveMarketingSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, marketingSettings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Marketing settings saved!");
    } catch { toast.error("Failed to save marketing settings"); }
  };

  const [klaviyoSyncing, setKlaviyoSyncing] = useState(false);

  const fetchKlaviyoStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/klaviyo/status`, { headers: { Authorization: `Bearer ${token}` } });
      setKlaviyoStatus(res.data);
    } catch {
      setKlaviyoStatus(null);
    }
  };

  const syncExistingToKlaviyo = async () => {
    if (!marketingSettings.klaviyo_list_id?.trim()) {
      toast.error("Please save a Klaviyo List ID first");
      return;
    }
    if (!window.confirm("Force-sync ALL subscribers to your Klaviyo list now? (Auto-sync already runs every 30 mins for any pending.)")) return;
    setKlaviyoSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/klaviyo/sync-existing`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const { synced, failed, total, errors, skipped_invalid, invalid_examples } = res.data;
      let summary = `Synced ${synced}/${total}.`;
      if (skipped_invalid > 0) summary += ` ${skipped_invalid} skipped (invalid email).`;
      if (failed > 0) summary += ` ${failed} failed.`;
      if (failed > 0 && errors?.length) {
        toast.error(`${summary} First error: ${errors[0]}`, { duration: 15000 });
        console.error("Klaviyo sync errors:", errors);
      } else if (synced > 0) {
        toast.success(summary, { duration: 8000 });
        if (skipped_invalid > 0) console.warn("Invalid emails skipped:", invalid_examples);
      } else {
        toast.error(summary, { duration: 10000 });
      }
      await fetchKlaviyoStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sync");
    }
    setKlaviyoSyncing(false);
  };

  const saveStripeKey = async () => {
    try {
      await axios.put(`${API}/admin/settings`, { stripe_api_key: stripeKeyEdit }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Stripe key saved!");
    } catch { toast.error("Failed to save Stripe key"); }
  };

  const searchPins = async () => {
    const q = pinSearch.trim().toLowerCase();
    if (!q) {
      setPinResults([]);
      return;
    }
    setPinSearching(true);
    try {
      const res = await axios.get(`${API}/pins`);
      const filtered = res.data.filter((p) => {
        const town = (p.town || "").toLowerCase();
        const country = (p.country || "").toLowerCase();
        const labels = (p.labels || []).join(" ").toLowerCase();
        const id = (p.id || "").toLowerCase();
        return town.includes(q) || country.includes(q) || labels.includes(q) || id.includes(q);
      }).slice(0, 50);
      setPinResults(filtered);
      if (filtered.length === 0) toast.info("No pins found matching that search");
    } catch { toast.error("Failed to search pins"); }
    setPinSearching(false);
  };

  const removePin = async (pinId) => {
    if (!window.confirm("Remove this pin? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/pins/${pinId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Pin removed");
      setPinResults(pinResults.filter((p) => p.id !== pinId));
      fetchData();
    } catch { toast.error("Failed to remove pin"); }
  };

  const [pinSecStatus, setPinSecStatus] = useState(null);
  const [pinSecLoading, setPinSecLoading] = useState(false);
  const checkPinSecurity = async () => {
    try {
      const res = await axios.get(`${API}/admin/pins/security-status`, { headers: { Authorization: `Bearer ${token}` } });
      setPinSecStatus(res.data);
    } catch { toast.error("Failed to check pin security"); }
  };
  const securePins = async () => {
    if (!window.confirm("Secure all existing pins? This will block external deletion of pins. Existing users who dropped pins before today's security update will no longer be able to self-delete their pins via 'Manage Pins' — but you can still remove them on request via Find & Remove a Pin.")) return;
    setPinSecLoading(true);
    try {
      const res = await axios.post(`${API}/admin/pins/secure-existing`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      await checkPinSecurity();
    } catch { toast.error("Failed to secure pins"); }
    setPinSecLoading(false);
  };

  const fetchLoginAttempts = async () => {
    setLoginAttemptsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/login-attempts`, { headers: { Authorization: `Bearer ${token}` } });
      setLoginAttempts(res.data.attempts || []);
    } catch { toast.error("Failed to load login attempts"); }
    setLoginAttemptsLoading(false);
  };

  const fetchMyIp = async () => {
    try {
      const res = await axios.get(`${API}/admin/my-ip`, { headers: { Authorization: `Bearer ${token}` } });
      setMyIp(res.data.ip || "");
    } catch { toast.error("Failed to detect IP"); }
  };

  const saveSecuritySettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, {
        admin_alert_email: securityEdit.admin_alert_email.trim(),
        admin_allowed_ips: securityEdit.admin_allowed_ips.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Security settings saved! Test by logging out + back in to confirm.");
    } catch { toast.error("Failed to save security settings"); }
  };

  const addMyIpToAllowlist = async () => {
    if (!myIp) { await fetchMyIp(); return; }
    const current = securityEdit.admin_allowed_ips.split(",").map(s => s.trim()).filter(Boolean);
    if (current.includes(myIp)) {
      toast.info("Your current IP is already in the allowlist");
      return;
    }
    const updated = [...current, myIp].join(", ");
    setSecurityEdit({ ...securityEdit, admin_allowed_ips: updated });
    toast.success("Your IP added — click Save to apply");
  };

  const fetchBackups = async () => {
    setBackupsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/backups`, { headers: { Authorization: `Bearer ${token}` } });
      setBackups(res.data.backups || []);
    } catch { toast.error("Failed to load backups"); }
    setBackupsLoading(false);
  };

  const runBackupNow = async () => {
    setBackupRunning(true);
    try {
      const res = await axios.post(`${API}/admin/backups/run`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Backup created: ${res.data.documents} documents, ${res.data.size_kb} KB`);
      await fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup failed");
    }
    setBackupRunning(false);
  };

  const downloadBackup = async (filename) => {
    try {
      const res = await fetch(`${API}/admin/backups/${filename}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download backup"); }
  };

  const getLabelColor = (labelId) => {
    const label = stats?.labels?.find((l) => l.id === labelId);
    return label?.color || "#6B7280";
  };

  const pendingRequests = labelRequests.filter((r) => r.status === "pending");
  const pendingEvents = events.filter((e) => !e.approved);
  const approvedEvents = events.filter((e) => e.approved);

  if (isLoading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8fafc" }} data-testid="loading-state">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="admin-container" data-testid="admin-dashboard">
      {/* Header */}
      <header className="admin-header" data-testid="admin-header">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-800" style={{ fontFamily: "Nunito" }}>Labelled</h1>
              <p className="text-xs text-slate-500">Admin Dashboard</p>
            </div>
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleExport("json")}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors font-medium text-sm"
            data-testid="export-json-btn"
          >
            <FileJson className="w-4 h-4" />
            <span className="hidden sm:inline">Export JSON</span>
          </button>
          <button
            onClick={() => handleExport("csv")}
            className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium text-sm"
            data-testid="export-csv-btn"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button
            onClick={fetchData}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 text-slate-600 ${isLoading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-4 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: "touch" }}>
            {[
              { id: "overview", label: "Overview", icon: MapPin },
              { id: "labels", label: `Labels${pendingRequests.length > 0 ? ` (${pendingRequests.length})` : ""}`, icon: Tag },
              { id: "events", label: `Events${pendingEvents.length > 0 ? ` (${pendingEvents.length})` : ""}`, icon: Calendar },
              { id: "areas", label: "Areas", icon: Users },
              { id: "subscribers", label: "Subscribers", icon: Mail },
              { id: "cookies", label: "Cookies", icon: Cookie },
              { id: "settings", label: "Settings", icon: Settings },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="admin-content">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
              <div className="stat-card" data-testid="total-pins-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h4>Total Pins</h4>
                </div>
                <div className="value">{stats?.total_pins || 0}</div>
                {stats?.total_pins > 0 && (
                  <button
                    onClick={async () => {
                      if (window.confirm("Are you sure you want to delete ALL pins? This cannot be undone.")) {
                        try {
                          await axios.delete(`${API}/admin/pins`, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success("All pins cleared");
                          fetchData();
                        } catch { toast.error("Failed to clear pins"); }
                      }
                    }}
                    className="mt-2 text-xs text-red-500 hover:text-red-700 font-medium"
                    data-testid="clear-all-pins-btn"
                  >
                    Clear all pins
                  </button>
                )}
              </div>

              <div className="stat-card" data-testid="total-areas-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h4>Areas Covered</h4>
                </div>
                <div className="value">{stats?.total_areas || 0}</div>
              </div>

              <div className="stat-card" data-testid="total-events-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <h4>Active Events</h4>
                </div>
                <div className="value">{stats?.total_events || 0}</div>
                {stats?.featured_events > 0 && (
                  <p className="text-xs text-amber-600 font-semibold mt-1">{stats.featured_events} featured</p>
                )}
              </div>

              <div className="stat-card" data-testid="pending-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <h4>Pending Reviews</h4>
                </div>
                <div className="value">{(stats?.pending_requests || 0) + (stats?.pending_events || 0)}</div>
              </div>
            </div>

            {/* Recent Failed Logins (security at-a-glance) */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mb-8" data-testid="recent-failed-logins-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                  <ShieldAlert className="w-5 h-5 text-rose-600" />
                  Recent Failed Logins (last 24h)
                </h2>
                <button onClick={fetchLoginAttempts} disabled={loginAttemptsLoading} className="btn-secondary text-xs" data-testid="refresh-overview-failed-logins-btn">
                  <RefreshCw className={`w-3 h-3 ${loginAttemptsLoading ? 'animate-spin' : ''}`} />
                  {loginAttemptsLoading ? "Loading..." : "Refresh"}
                </button>
              </div>
              {(() => {
                const cutoff = Date.now() - 24 * 60 * 60 * 1000;
                const recent = (loginAttempts || []).filter(a => !a.success && a.attempted_at && new Date(a.attempted_at).getTime() >= cutoff);
                if (recent.length === 0) {
                  return <p className="text-sm text-emerald-700">No failed logins in the last 24 hours. All clear.</p>;
                }
                const ipBlocked = recent.filter(a => a.reason === "ip_blocked").length;
                const lockedOut = recent.filter(a => a.reason === "locked_out").length;
                const badPassword = recent.length - ipBlocked - lockedOut;
                return (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
                      <div className="p-2 rounded-lg bg-amber-50 border border-amber-200" data-testid="bad-password-count">
                        <div className="text-amber-700 font-semibold">{badPassword}</div>
                        <div className="text-slate-600">Wrong password</div>
                      </div>
                      <div className="p-2 rounded-lg bg-rose-50 border border-rose-200" data-testid="ip-blocked-count">
                        <div className="text-rose-700 font-semibold">{ipBlocked}</div>
                        <div className="text-slate-600">IP not allowlisted</div>
                      </div>
                      <div className="p-2 rounded-lg bg-orange-50 border border-orange-200" data-testid="locked-out-count">
                        <div className="text-orange-700 font-semibold">{lockedOut}</div>
                        <div className="text-slate-600">Lockout triggered</div>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-left text-slate-500 border-b border-slate-200">
                          <tr>
                            <th className="py-2 pr-3">When</th>
                            <th className="py-2 pr-3">Username tried</th>
                            <th className="py-2 pr-3">IP</th>
                            <th className="py-2">Reason</th>
                          </tr>
                        </thead>
                        <tbody data-testid="failed-logins-tbody">
                          {recent.slice(0, 10).map((a, i) => (
                            <tr key={i} className="border-b border-slate-100">
                              <td className="py-2 pr-3 text-slate-700">{new Date(a.attempted_at).toLocaleString()}</td>
                              <td className="py-2 pr-3 text-slate-700">{a.username || "—"}</td>
                              <td className="py-2 pr-3 font-mono text-slate-600">{a.ip || "—"}</td>
                              <td className="py-2">
                                {a.reason === "ip_blocked" ? <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-700">IP blocked</span>
                                  : a.reason === "locked_out" ? <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-700">Locked out</span>
                                  : <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">Wrong password</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {recent.length > 10 && (
                      <p className="text-xs text-slate-500 mt-2">Showing 10 of {recent.length}. See full audit log in Settings → Login Attempts.</p>
                    )}
                  </>
                );
              })()}
            </section>

            {/* Pin Lookup & Remove */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mb-8" data-testid="pin-lookup-section">
              <h2 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <MapPin className="w-5 h-5 text-indigo-600" />
                Find & Remove a Pin
              </h2>

              {/* Pin Security */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="pin-security-bar">
                <div className="text-sm">
                  <span className="font-semibold text-amber-800">Pin security:</span>
                  {pinSecStatus ? (
                    <span className="ml-2 text-amber-700">
                      {pinSecStatus.protected}/{pinSecStatus.total} protected · {pinSecStatus.unprotected} unprotected
                    </span>
                  ) : (
                    <span className="ml-2 text-slate-500 italic">click "Check" to see status</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={checkPinSecurity} className="btn-secondary text-xs" data-testid="check-pin-security-btn">
                    <RefreshCw className="w-3 h-3" />
                    Check
                  </button>
                  {pinSecStatus && pinSecStatus.unprotected > 0 && (
                    <button onClick={securePins} disabled={pinSecLoading} className="btn-primary text-xs" data-testid="secure-pins-btn">
                      <Lock className={`w-3 h-3 ${pinSecLoading ? 'animate-pulse' : ''}`} />
                      {pinSecLoading ? "Securing..." : `Secure ${pinSecStatus.unprotected} pin${pinSecStatus.unprotected === 1 ? '' : 's'}`}
                    </button>
                  )}
                </div>
              </div>

              <p className="text-sm text-slate-500 mb-4">Search by town, country, label, or pin ID. Useful when a user can't remove their own pin and asks for help.</p>
              <div className="flex gap-2 max-w-2xl">
                <input
                  type="text"
                  value={pinSearch}
                  onChange={(e) => setPinSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") searchPins(); }}
                  placeholder="Search e.g. 'London' or 'adhd' or pin ID..."
                  className="input-field flex-1"
                  data-testid="pin-search-input"
                />
                <button onClick={searchPins} disabled={pinSearching} className="btn-primary text-sm shrink-0" data-testid="pin-search-btn">
                  <RefreshCw className={`w-4 h-4 ${pinSearching ? 'animate-spin' : ''}`} />
                  Search
                </button>
              </div>
              {pinResults.length > 0 && (
                <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                  <p className="text-xs font-semibold text-slate-500">{pinResults.length} pin{pinResults.length === 1 ? '' : 's'} found:</p>
                  {pinResults.map((pin) => (
                    <div key={pin.id} className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-xl" data-testid={`pin-result-${pin.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm">{pin.town}, {pin.country}</p>
                        <p className="text-xs text-slate-500 truncate">
                          Labels: {(pin.labels || []).join(", ") || "none"} • Added {pin.created_at ? new Date(pin.created_at).toLocaleDateString() : "-"}
                        </p>
                        <p className="text-[10px] text-slate-400 font-mono truncate" title={pin.id}>ID: {pin.id}</p>
                      </div>
                      <button
                        onClick={() => removePin(pin.id)}
                        className="px-3 py-1.5 text-xs font-semibold text-red-600 hover:text-white hover:bg-red-600 border border-red-200 rounded-lg transition-colors shrink-0"
                        data-testid={`remove-pin-${pin.id}`}
                      >
                        <Trash2 className="w-3 h-3 inline mr-1" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Active Labels */}
            <section data-testid="active-labels-section">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Tag className="w-5 h-5 text-pink-600" />
                Active Labels ({stats?.labels?.length || 0})
              </h2>
              <div className="flex flex-wrap gap-2">
                {stats?.labels?.map((label) => (
                  <span key={label.id} className="label-badge" style={{ backgroundColor: label.color }} data-testid={`active-label-${label.id}`}>
                    {label.name}
                  </span>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Labels Tab */}
        {activeTab === "labels" && (
          <section data-testid="label-requests-section">
            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
              <Tag className="w-5 h-5 text-indigo-600" />
              Label Requests
            </h2>

            {pendingRequests.length === 0 ? (
              <div className="bg-white rounded-2xl p-6 text-center border border-slate-100">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-slate-500">No pending label requests</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingRequests.map((request) => (
                  <div key={request.id} className="request-card" data-testid={`request-card-${request.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>{request.name}</h4>
                        <p className="text-sm text-slate-500 mt-1">{request.reason}</p>
                      </div>
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">Pending</span>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-slate-500 mb-2">Select color:</p>
                      <div className="color-picker">
                        {COLOR_OPTIONS.map((color) => (
                          <button
                            key={color}
                            onClick={() => setSelectedColor((prev) => ({ ...prev, [request.id]: color }))}
                            className={`color-option ${selectedColor[request.id] === color ? "selected" : ""}`}
                            style={{ backgroundColor: color }}
                            data-testid={`color-${request.id}-${color}`}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRequestAction(request.id, "approve")}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors"
                        data-testid={`approve-btn-${request.id}`}
                      >
                        <Check className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleRequestAction(request.id, "reject")}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors"
                        data-testid={`reject-btn-${request.id}`}
                      >
                        <X className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {labelRequests.filter((r) => r.status !== "pending").length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-semibold text-slate-500 mb-3">Previous Requests</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {labelRequests.filter((r) => r.status !== "pending").slice(0, 9).map((request) => (
                    <div key={request.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`prev-request-${request.id}`}>
                      <span className="font-medium text-slate-700">{request.name}</span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${request.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {request.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Events Tab */}
        {activeTab === "events" && (
          <section data-testid="events-section">
            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
              <Calendar className="w-5 h-5 text-emerald-600" />
              Community Events
            </h2>

            {/* Pending Events */}
            {pendingEvents.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-amber-600 uppercase tracking-wide mb-3">
                  Pending Approval ({pendingEvents.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {pendingEvents.map((event) => (
                    <div key={event.id} className="bg-amber-50 rounded-2xl p-4 border border-amber-200" data-testid={`pending-event-${event.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>{event.title}</h4>
                          <p className="text-sm text-slate-600">{event.address || `${event.town}, ${event.country}`}</p>
                        </div>
                        <span className="px-2 py-1 bg-amber-200 text-amber-800 text-xs font-semibold rounded-full">
                          {event.event_type}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{event.description}</p>
                      {event.event_date && <p className="text-xs text-slate-500 mb-1">Date: {event.event_date}{event.recurring && event.end_date ? ` — ${event.end_date}` : ""}</p>}
                      {event.recurring && event.schedule && <p className="text-xs text-slate-500 mb-1">Schedule: {event.schedule}</p>}
                      <p className="text-xs text-slate-500 mb-1">Contact: {event.contact_info}</p>
                      {event.poster_email && <p className="text-xs text-slate-400 mb-3">Poster: {event.poster_email}</p>}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEventAction(event.id, "approve")}
                          className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors"
                          data-testid={`approve-event-${event.id}`}
                        >
                          <Check className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => openEventActionModal(event.id, "reject", event.title)}
                          className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors"
                          data-testid={`reject-event-${event.id}`}
                        >
                          <X className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approved Events */}
            <div>
              <h3 className="text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-3">
                Active Events ({approvedEvents.length})
              </h3>
              {approvedEvents.length === 0 ? (
                <div className="bg-white rounded-2xl p-6 text-center border border-slate-100">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <Calendar className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-500">No active events yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {approvedEvents.map((event) => (
                    <div key={event.id} className="bg-white rounded-2xl p-4 border border-slate-100 hover:shadow-md transition-shadow" data-testid={`approved-event-${event.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>{event.title}</h4>
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                          {event.event_type}
                        </span>
                      </div>
                      <p className="text-sm text-indigo-600 font-medium mb-1">{event.address || `${event.town}, ${event.country}`}</p>
                      {event.event_date && <p className="text-xs text-slate-500 mb-1">Date: {event.event_date}{event.recurring && event.end_date ? ` — ${event.end_date}` : ""}</p>}
                      {event.recurring && event.schedule && <p className="text-xs text-slate-500 mb-1">Schedule: {event.schedule}</p>}
                      <p className="text-sm text-slate-600 mb-1">{event.description}</p>
                      <p className="text-xs text-slate-500 mb-1">Contact: {event.contact_info}</p>
                      {event.poster_email && <p className="text-xs text-slate-400 mb-3">Poster: {event.poster_email}</p>}
                      <button
                        onClick={() => openEventActionModal(event.id, "delete", event.title)}
                        className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded-xl transition-colors"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove Event
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Event Action Modal (Reject / Delete with reason) */}
            {eventActionModal.show && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="event-action-modal">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                  <h3 className="font-bold text-lg text-slate-800 mb-2" style={{ fontFamily: "Nunito" }}>
                    {eventActionModal.action === "reject" ? "Reject Event" : "Remove Event"}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {eventActionModal.action === "reject"
                      ? `Reject "${eventActionModal.eventTitle}"? The poster will be notified via email.`
                      : `Remove "${eventActionModal.eventTitle}"? The poster will be notified via email.`}
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Reason {eventActionModal.action === "reject" ? "for rejection" : "for removal"} *
                    </label>
                    <textarea
                      value={eventActionModal.reason}
                      onChange={(e) => setEventActionModal({ ...eventActionModal, reason: e.target.value })}
                      placeholder={eventActionModal.action === "reject" ? "e.g., Insufficient details, not relevant to the community..." : "e.g., Event no longer appropriate, reported by users..."}
                      className="w-full px-3 py-2 bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 rounded-xl text-sm min-h-[100px] resize-y"
                      data-testid="event-action-reason-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setEventActionModal({ show: false, eventId: null, action: "", eventTitle: "", reason: "" })}
                      className="flex-1 py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors"
                      data-testid="event-action-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (!eventActionModal.reason.trim()) {
                          toast.error("Please provide a reason");
                          return;
                        }
                        if (eventActionModal.action === "reject") {
                          handleEventAction(eventActionModal.eventId, "reject", eventActionModal.reason);
                        } else {
                          handleDeleteEvent(eventActionModal.eventId, eventActionModal.reason);
                        }
                      }}
                      className={`flex-1 py-2 px-4 font-semibold rounded-xl transition-colors text-white ${
                        eventActionModal.action === "reject" ? "bg-slate-700 hover:bg-slate-800" : "bg-red-500 hover:bg-red-600"
                      }`}
                      data-testid="event-action-confirm-btn"
                    >
                      {eventActionModal.action === "reject" ? "Reject & Notify" : "Remove & Notify"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Areas Tab */}
        {activeTab === "areas" && (
          <section data-testid="areas-section">
            <h2 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
              <Users className="w-5 h-5 text-emerald-600" />
              Potential Group Locations
            </h2>
            <p className="text-slate-500 text-sm mb-6">
              Areas with the highest concentration of pins - ideal for forming local support groups. Click share to get a shareable link.
            </p>

            {stats?.top_areas?.length === 0 ? (
              <div className="bg-white rounded-2xl p-6 text-center border border-slate-100">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-slate-500">No pins added yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats?.top_areas?.map((area, idx) => (
                  <div key={idx} className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={`area-card-${idx}`}>
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                          <span className="text-white font-bold text-lg" style={{ fontFamily: "Nunito" }}>{area.total_pins}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>{area.town}</h4>
                          <p className="text-sm text-slate-500">{area.country}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => shareAreaLink(area)}
                          className="p-2 hover:bg-indigo-50 rounded-lg transition-colors text-indigo-600"
                          data-testid={`share-area-${idx}`}
                        >
                          <Share2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setExpandedArea(expandedArea === idx ? null : idx)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          data-testid={`expand-area-${idx}`}
                        >
                          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedArea === idx ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    </div>

                    {expandedArea === idx && (
                      <div className="px-4 pb-4 animate-fade-in">
                        <div className="pt-3 border-t border-slate-100">
                          <p className="text-xs font-semibold text-slate-500 mb-2">Community Breakdown</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(area.label_counts || {}).map(([labelId, count]) => (
                              <span key={labelId} className="label-badge text-xs" style={{ backgroundColor: getLabelColor(labelId) }}>
                                {getLabelName(labelId)}: {count}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Subscribers Tab */}
        {activeTab === "subscribers" && (
          <section data-testid="subscribers-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Mail className="w-5 h-5 text-pink-600" />
                Email Subscribers
              </h2>
              <div className="flex items-center gap-2">
                {subscribers.total > 0 && (
                  <a
                    href={`${API}/admin/subscribers/export`}
                    onClick={(e) => {
                      e.preventDefault();
                      const link = document.createElement("a");
                      fetch(`${API}/admin/subscribers/export`, { headers: { Authorization: `Bearer ${token}` } })
                        .then(r => r.blob())
                        .then(blob => {
                          link.href = URL.createObjectURL(blob);
                          link.download = "neuromap_subscribers.csv";
                          link.click();
                          toast.success("CSV downloaded!");
                        })
                        .catch(() => toast.error("Failed to download CSV"));
                    }}
                    className="btn-secondary text-sm py-2 px-4"
                    data-testid="download-subscribers-csv"
                  >
                    <Download className="w-4 h-4" />
                    Download CSV
                  </a>
                )}
                <button onClick={fetchSubscribers} className="btn-secondary text-sm py-2 px-4" data-testid="load-subscribers-btn">
                  <RefreshCw className="w-4 h-4" />
                  Load / Refresh
                </button>
              </div>
            </div>

            {subscribers.total > 0 ? (
              <>
                <div className="bg-indigo-50 rounded-xl p-4 mb-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-700">{subscribers.total}</p>
                    <p className="text-sm text-indigo-600">Total subscribers</p>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Subscribed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.subscribers.map((sub, idx) => (
                        <tr key={sub.id || idx} className="border-b border-slate-100 last:border-0" data-testid={`subscriber-row-${idx}`}>
                          <td className="py-3 px-4 font-medium text-slate-800">{sub.first_name}</td>
                          <td className="py-3 px-4 text-slate-600">{sub.email}</td>
                          <td className="py-3 px-4 text-slate-500 text-xs">{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-slate-500 mb-2">No subscribers yet</p>
                <p className="text-xs text-slate-400">Click "Load / Refresh" to fetch, or subscribers will appear here once users sign up during pin drops.</p>
              </div>
            )}
          </section>
        )}

        {/* Cookie Analytics Tab */}
        {activeTab === "cookies" && (
          <section data-testid="cookies-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Cookie className="w-5 h-5 text-amber-600" />
                Cookie Consent Analytics
              </h2>
              <button onClick={fetchCookieAnalytics} className="btn-secondary text-sm py-2 px-4" data-testid="load-cookies-btn">
                <RefreshCw className="w-4 h-4" />
                Load / Refresh
              </button>
            </div>

            {cookieAnalytics ? (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-6 border border-slate-100">
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Consent Overview</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-slate-800">{cookieAnalytics.total_responses}</p>
                      <p className="text-sm text-slate-500">Total Responses</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-600">{cookieAnalytics.analytics_rate}%</p>
                      <p className="text-sm text-emerald-700">Analytics Accepted</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">{cookieAnalytics.marketing_rate}%</p>
                      <p className="text-sm text-purple-700">Marketing Accepted</p>
                    </div>
                  </div>

                  {/* Breakdown bars */}
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700">Analytics Cookies</span>
                        <span className="text-sm text-slate-500">{cookieAnalytics.analytics_accepted} / {cookieAnalytics.total_responses}</span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${cookieAnalytics.analytics_rate}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700">Marketing Cookies</span>
                        <span className="text-sm text-slate-500">{cookieAnalytics.marketing_accepted} / {cookieAnalytics.total_responses}</span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${cookieAnalytics.marketing_rate}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-slate-500 mb-2">No data loaded yet</p>
                <p className="text-xs text-slate-400">Click "Load / Refresh" to see how users are responding to your cookie banner.</p>
              </div>
            )}
          </section>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6" data-testid="settings-tab">
            {/* Branding */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Settings className="w-5 h-5 text-indigo-600" />
                Branding
              </h3>
              <div className="space-y-4 max-w-lg">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">App Name</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={appNameEdit}
                      onChange={(e) => setAppNameEdit(e.target.value)}
                      placeholder="Labelled"
                      className="input-field flex-1"
                      data-testid="app-name-input"
                    />
                    <button onClick={saveAppName} className="btn-primary text-sm shrink-0" data-testid="save-app-name-btn">
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Displayed in the header and panels across the app.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Hero Tagline</label>
                  <div className="flex gap-2">
                    <textarea
                      value={heroTaglineEdit}
                      onChange={(e) => setHeroTaglineEdit(e.target.value)}
                      placeholder="Connect with neurodivergent people in your area. Add your pin to join the map!"
                      className="w-full px-3 py-2 bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 rounded-xl text-sm min-h-[60px] resize-y flex-1"
                      data-testid="hero-tagline-input"
                    />
                    <button onClick={saveHeroTagline} className="btn-primary text-sm shrink-0 self-start" data-testid="save-hero-tagline-btn">
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">The main message shown on the map page below the app name.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Label Request Button Text</label>
                  <div className="flex gap-2">
                    <textarea
                      value={labelRequestTextEdit}
                      onChange={(e) => setLabelRequestTextEdit(e.target.value)}
                      placeholder="e.g., We can only add labels for recognised diagnoses. If yours isn't listed, let us know."
                      className="w-full px-3 py-2 bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 rounded-xl text-sm min-h-[60px] resize-y flex-1"
                      data-testid="label-request-text-input"
                    />
                    <button onClick={saveLabelRequestText} className="btn-primary text-sm shrink-0 self-start" data-testid="save-label-request-text-btn">
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Subtext shown above the "Request New Label" button on the map page.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Privacy Policy</label>
                  <p className="text-xs text-slate-500 mb-2">Edit your privacy policy content. Supports Markdown — use <code>## Heading</code>, <code>**bold**</code>, <code>- list item</code>, <code>[link](url)</code>. Leave blank to use the default policy.</p>
                  <textarea
                    value={privacyPolicyEdit}
                    onChange={(e) => setPrivacyPolicyEdit(e.target.value)}
                    placeholder={`## 1. Who We Are\nLabelled is a community mapping platform...\n\n## 2. What Data We Collect\n- Pin Data: town/area + labels (no exact location)\n- Email (optional, with consent)\n\n## Contact\nReach us at hello@example.com`}
                    className="w-full px-3 py-2 bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 rounded-xl text-sm min-h-[280px] font-mono resize-y"
                    data-testid="privacy-policy-input"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={savePrivacyPolicy} className="btn-primary text-sm" data-testid="save-privacy-policy-btn">
                      <Save className="w-4 h-4" />
                      Save Privacy Policy
                    </button>
                    <Link to="/privacy" target="_blank" className="btn-secondary text-sm" data-testid="preview-privacy-btn">
                      Preview
                    </Link>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Custom Logo</label>
                  <p className="text-xs text-slate-500 mb-2">Upload a logo image (PNG, JPG, SVG — max 2MB). Displayed in the navbar across the app.</p>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50">
                      <img
                        id="logo-preview"
                        src={`${API}/logo?t=${Date.now()}`}
                        alt=""
                        className="w-full h-full object-contain"
                        onError={(e) => { e.target.style.display = "none"; }}
                        onLoad={(e) => { e.target.style.display = "block"; }}
                      />
                      <Image className="w-6 h-6 text-slate-300" />
                    </div>
                    <button onClick={() => document.getElementById('logo-file-input').click()} className="btn-secondary text-sm py-2 px-4 cursor-pointer" data-testid="upload-logo-btn" disabled={logoUploading}>
                      <Image className="w-4 h-4" />
                      {logoUploading ? "Uploading..." : "Upload Logo"}
                    </button>
                    <input id="logo-file-input" type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </div>
                </div>
              </div>
            </section>

            {/* Social Links */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Share2 className="w-5 h-5 text-indigo-600" />
                Social Links
              </h3>
              <p className="text-sm text-slate-500 mb-4">Shown as small icons at the bottom of the "Add Your Pin" panel. Leave a field blank to hide that icon.</p>
              <div className="space-y-4 max-w-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!socialEdit.show_social_links}
                    onChange={(e) => setSocialEdit({ ...socialEdit, show_social_links: e.target.checked })}
                    className="w-5 h-5 accent-indigo-600"
                    data-testid="show-social-links-toggle"
                  />
                  <span className="text-sm font-semibold text-slate-700">Show social icons on the map panel</span>
                </label>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">TikTok URL</label>
                  <input
                    type="text"
                    value={socialEdit.social_tiktok_url}
                    onChange={(e) => setSocialEdit({ ...socialEdit, social_tiktok_url: e.target.value })}
                    placeholder="https://www.tiktok.com/@yourhandle"
                    className="input-field"
                    data-testid="social-tiktok-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Instagram URL</label>
                  <input
                    type="text"
                    value={socialEdit.social_instagram_url}
                    onChange={(e) => setSocialEdit({ ...socialEdit, social_instagram_url: e.target.value })}
                    placeholder="https://www.instagram.com/yourhandle"
                    className="input-field"
                    data-testid="social-instagram-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">YouTube URL</label>
                  <input
                    type="text"
                    value={socialEdit.social_youtube_url}
                    onChange={(e) => setSocialEdit({ ...socialEdit, social_youtube_url: e.target.value })}
                    placeholder="https://www.youtube.com/@yourchannel"
                    className="input-field"
                    data-testid="social-youtube-input"
                  />
                </div>
                <button onClick={saveSocialLinks} className="btn-primary text-sm" data-testid="save-social-links-btn">
                  <Save className="w-4 h-4" />
                  Save Social Links
                </button>
              </div>
            </section>
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <CreditCard className="w-5 h-5 text-indigo-600" />
                Payments (Stripe)
              </h3>
              <p className="text-sm text-slate-500 mb-4">Your Stripe secret key is used for featured event payments. Get it from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline font-medium">dashboard.stripe.com/apikeys</a>.</p>
              <div className="space-y-3 max-w-lg">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Stripe Secret Key</label>
                  <input
                    type="password"
                    value={stripeKeyEdit}
                    onChange={(e) => setStripeKeyEdit(e.target.value)}
                    placeholder="sk_live_xxxxxxxxxxxxxxxx"
                    className="input-field"
                    data-testid="stripe-key-input"
                  />
                </div>
                <button onClick={saveStripeKey} className="btn-primary text-sm" data-testid="save-stripe-btn">
                  <Save className="w-4 h-4" />
                  Save Stripe Key
                </button>
              </div>
            </section>

            {/* Marketing & Tracking Settings */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Globe className="w-5 h-5 text-indigo-600" />
                Marketing & Tracking
              </h3>
              <p className="text-sm text-slate-500 mb-4">Connect your email marketing and ad tracking services. IDs/keys are saved securely and used server-side.</p>
              <div className="space-y-4 max-w-lg">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Klaviyo API Key</label>
                  <p className="text-xs text-slate-500 mb-1">Used to sync email subscribers. Find it in Klaviyo &rarr; Account &rarr; Settings &rarr; API Keys.</p>
                  <input
                    type="password"
                    value={marketingSettings.klaviyo_api_key}
                    onChange={(e) => setMarketingSettings({ ...marketingSettings, klaviyo_api_key: e.target.value })}
                    placeholder="pk_xxxxxxxxxxxxxxxx"
                    className="input-field"
                    data-testid="klaviyo-key-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Klaviyo List ID</label>
                  <p className="text-xs text-slate-500 mb-1">The list new subscribers are added to (this is what triggers your welcome flow). Find it in your list URL: <code>klaviyo.com/list/<strong>YOUR_ID</strong></code></p>
                  <input
                    type="text"
                    value={marketingSettings.klaviyo_list_id}
                    onChange={(e) => setMarketingSettings({ ...marketingSettings, klaviyo_list_id: e.target.value })}
                    placeholder="e.g. SG7xRr"
                    className="input-field"
                    data-testid="klaviyo-list-id-input"
                  />
                  <button
                    onClick={syncExistingToKlaviyo}
                    disabled={klaviyoSyncing}
                    className="btn-secondary text-xs mt-2"
                    data-testid="klaviyo-sync-existing-btn"
                  >
                    <RefreshCw className={`w-3 h-3 ${klaviyoSyncing ? 'animate-spin' : ''}`} />
                    {klaviyoSyncing ? "Syncing..." : "Force sync all subscribers now"}
                  </button>

                  {/* Auto-sync status panel */}
                  <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs space-y-1" data-testid="klaviyo-sync-status">
                    <div className="flex items-center gap-2 font-semibold text-emerald-800">
                      <RefreshCw className="w-3 h-3" /> Auto-sync is on
                    </div>
                    <p className="text-emerald-700">New subscribers sync instantly. A background job re-tries any failures every 30 minutes — so you should never need the button above.</p>
                    {klaviyoStatus ? (
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-slate-700">
                        <div><span className="text-slate-500">Total subscribers:</span> <strong>{klaviyoStatus.total_subscribers}</strong></div>
                        <div><span className="text-slate-500">Pending:</span> <strong className={klaviyoStatus.pending_count > 0 ? "text-amber-700" : "text-emerald-700"}>{klaviyoStatus.pending_count}</strong></div>
                        <div className="col-span-2">
                          <span className="text-slate-500">Last sync:</span>{" "}
                          <strong>{klaviyoStatus.last_sync_at ? new Date(klaviyoStatus.last_sync_at).toLocaleString() : "never"}</strong>
                          {klaviyoStatus.last_sync_mode && (
                            <span className="text-slate-500"> ({klaviyoStatus.last_sync_mode}{typeof klaviyoStatus.last_sync_synced === 'number' ? ` · ${klaviyoStatus.last_sync_synced} synced, ${klaviyoStatus.last_sync_failed || 0} failed` : ""})</span>
                          )}
                        </div>
                        {!klaviyoStatus.configured && (
                          <div className="col-span-2 text-amber-700">Klaviyo API key or list ID missing — auto-sync is paused until both are saved.</div>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500">Loading status…</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Meta (Facebook) Pixel ID</label>
                  <p className="text-xs text-slate-500 mb-1">Only loaded if users consent to marketing cookies.</p>
                  <input
                    type="text"
                    value={marketingSettings.meta_pixel_id}
                    onChange={(e) => setMarketingSettings({ ...marketingSettings, meta_pixel_id: e.target.value })}
                    placeholder="123456789012345"
                    className="input-field"
                    data-testid="meta-pixel-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">TikTok Pixel ID</label>
                  <p className="text-xs text-slate-500 mb-1">Only loaded if users consent to marketing cookies.</p>
                  <input
                    type="text"
                    value={marketingSettings.tiktok_pixel_id}
                    onChange={(e) => setMarketingSettings({ ...marketingSettings, tiktok_pixel_id: e.target.value })}
                    placeholder="CXXXXXXXXXXXXXXXXX"
                    className="input-field"
                    data-testid="tiktok-pixel-input"
                  />
                </div>
                <button onClick={saveMarketingSettings} className="btn-primary text-sm" data-testid="save-marketing-btn">
                  <Save className="w-4 h-4" />
                  Save Marketing Settings
                </button>
              </div>
            </section>

            {/* Feature Toggles */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Settings className="w-5 h-5 text-indigo-600" />
                Feature Toggles
              </h3>
              <p className="text-sm text-slate-500 mb-4">Turn features on or off across the entire app.</p>
              <div className="space-y-4">
                {settings && [
                  { key: "enable_pin_dropping", label: "Pin Dropping", desc: "Allow users to add pins to the map" },
                  { key: "enable_event_submissions", label: "Event Submissions", desc: "Allow users to submit community events" },
                  { key: "enable_featured_listings", label: "Featured Listings (Paid)", desc: "Allow hosts to pay for featured event placement" },
                  { key: "enable_label_requests", label: "Label Requests", desc: "Allow users to request new diagnosis labels" },
                  { key: "enable_calendar", label: "Event Calendar", desc: "Show the calendar page link in the navigation" },
                  { key: "enable_host_portal", label: "Host Portal", desc: "Show the host portal link in the navigation" },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between py-3 px-4 rounded-xl bg-slate-50" data-testid={`toggle-${key}`}>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{label}</p>
                      <p className="text-xs text-slate-500">{desc}</p>
                    </div>
                    <button onClick={() => toggleSetting(key)} className="focus:outline-none" data-testid={`toggle-btn-${key}`}>
                      {settings[key] ? (
                        <ToggleRight className="w-10 h-10 text-emerald-500" />
                      ) : (
                        <ToggleLeft className="w-10 h-10 text-slate-300" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* Label Management */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Palette className="w-5 h-5 text-indigo-600" />
                Manage Labels
              </h3>
              {/* Add New Label */}
              <div className="flex gap-2 mb-4 items-end" data-testid="add-label-form">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Label Name</label>
                  <input type="text" value={newLabelName} onChange={(e) => setNewLabelName(e.target.value)} placeholder="e.g. Dyscalculia" className="input-field" data-testid="new-label-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Color</label>
                  <input type="color" value={newLabelColor} onChange={(e) => setNewLabelColor(e.target.value)} className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer" data-testid="new-label-color-input" />
                </div>
                <button onClick={handleAddLabel} className="btn-primary text-sm py-2 px-4" data-testid="add-label-btn">
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
              {/* Existing Labels */}
              <div className="space-y-2">
                {stats?.labels?.map((label) => (
                  <div key={label.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50" data-testid={`label-row-${label.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-white shadow-sm" style={{ background: label.color }} />
                      {editingLabel === label.id ? (
                        <div className="flex gap-2 items-center">
                          <input type="text" defaultValue={label.name} id={`edit-name-${label.id}`} className="input-field text-sm py-1 w-32" />
                          <input type="color" defaultValue={label.color} id={`edit-color-${label.id}`} className="w-8 h-8 rounded border cursor-pointer" />
                          <button onClick={() => {
                            const name = document.getElementById(`edit-name-${label.id}`).value;
                            const color = document.getElementById(`edit-color-${label.id}`).value;
                            handleUpdateLabel(label.id, { name, color });
                          }} className="text-emerald-600 hover:text-emerald-700 p-1"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingLabel(null)} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <span className="font-medium text-slate-800 text-sm">{label.name}</span>
                      )}
                    </div>
                    {editingLabel !== label.id && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditingLabel(label.id)} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-500" data-testid={`edit-label-${label.id}`}><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteLabel(label.id)} className="p-1.5 hover:bg-red-100 rounded-lg text-red-400 hover:text-red-600" data-testid={`delete-label-${label.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Stripe Status */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <CreditCard className="w-5 h-5 text-indigo-600" />
                Stripe Payment Status
              </h3>
              {stripeStatus ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 py-3 px-4 rounded-xl bg-slate-50">
                    <div className={`w-3 h-3 rounded-full ${stripeStatus.status === "active" ? "bg-emerald-500" : "bg-red-500"}`} />
                    <span className="font-semibold text-sm text-slate-800">
                      {stripeStatus.status === "active" ? "Connected & Active" : "Not Connected"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-slate-800">{stripeStatus.total_transactions}</p>
                      <p className="text-xs text-slate-500">Total Transactions</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{stripeStatus.paid_transactions}</p>
                      <p className="text-xs text-slate-500">Successful Payments</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Key: {stripeStatus.key_prefix}</p>
                </div>
              ) : (
                <button onClick={fetchStripeStatus} className="btn-primary text-sm" data-testid="check-stripe-btn">
                  <CreditCard className="w-4 h-4" />
                  Check Stripe Connection
                </button>
              )}
            </section>

            {/* Change Password */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Lock className="w-5 h-5 text-indigo-600" />
                Change Admin Password
              </h3>
              <div className="space-y-3 max-w-md">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Current Password</label>
                  <input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})} className="input-field" data-testid="current-password-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">New Password</label>
                  <input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} className="input-field" data-testid="new-password-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Confirm New Password</label>
                  <input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})} className="input-field" data-testid="confirm-password-input" />
                </div>
                <button onClick={handleChangePassword} className="btn-primary text-sm" data-testid="change-password-btn">
                  <Lock className="w-4 h-4" />
                  Change Password
                </button>
                <p className="text-xs text-slate-500 mt-2">Must be at least 12 characters with uppercase, lowercase, and a number.</p>
              </div>
            </section>

            {/* Security: Login Alerts & IP Allowlist */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="security-section">
              <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                <Lock className="w-5 h-5 text-indigo-600" />
                Admin Login Security
              </h3>
              <div className="space-y-5 max-w-2xl">

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Login Alert Email</label>
                  <p className="text-xs text-slate-500 mb-2">You'll receive a Klaviyo event (set up a flow in Klaviyo to email yourself) every time the admin logs in successfully — with the IP and time. Requires Klaviyo API key.</p>
                  <input
                    type="email"
                    value={securityEdit.admin_alert_email}
                    onChange={(e) => setSecurityEdit({ ...securityEdit, admin_alert_email: e.target.value })}
                    placeholder="you@example.com"
                    className="input-field"
                    data-testid="admin-alert-email-input"
                  />
                  <p className="text-xs text-amber-600 mt-1">⚠️ In Klaviyo, you must create a flow triggered by the metric named <code>Admin Login Alert</code> sending to this address. Once a day for changed IPs is fine.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">IP Allowlist (Optional)</label>
                  <p className="text-xs text-slate-500 mb-2">If set, only the IP addresses listed below can log in to admin. Leave blank to allow login from any IP. Use commas for multiple. Use <strong>only when you know your IP doesn't change</strong>.</p>
                  <div className="flex items-center gap-2 mb-2">
                    <button onClick={fetchMyIp} type="button" className="btn-secondary text-xs" data-testid="detect-ip-btn">
                      <RefreshCw className="w-3 h-3" />
                      Detect my IP
                    </button>
                    {myIp && (
                      <>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">{myIp}</code>
                        <button onClick={addMyIpToAllowlist} type="button" className="btn-primary text-xs" data-testid="add-my-ip-btn">
                          Add to allowlist
                        </button>
                      </>
                    )}
                  </div>
                  <input
                    type="text"
                    value={securityEdit.admin_allowed_ips}
                    onChange={(e) => setSecurityEdit({ ...securityEdit, admin_allowed_ips: e.target.value })}
                    placeholder="e.g. 81.110.206.238, 192.0.2.7"
                    className="input-field font-mono text-sm"
                    data-testid="admin-allowed-ips-input"
                  />
                  <p className="text-xs text-red-600 mt-2 font-semibold">⚠️ If you set this wrong, you'll lock yourself out. To recover, ask the developer to clear <code>admin_allowed_ips</code> directly in the database, OR access from one of the listed IPs.</p>
                </div>

                <button onClick={saveSecuritySettings} className="btn-primary text-sm" data-testid="save-security-btn">
                  <Save className="w-4 h-4" />
                  Save Security Settings
                </button>
              </div>
            </section>

            {/* Login Attempts Audit */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                  <Lock className="w-5 h-5 text-indigo-600" />
                  Login Attempts (Audit Log)
                </h3>
                <button onClick={fetchLoginAttempts} disabled={loginAttemptsLoading} className="btn-secondary text-sm" data-testid="refresh-login-attempts-btn">
                  <RefreshCw className={`w-4 h-4 ${loginAttemptsLoading ? 'animate-spin' : ''}`} />
                  Load / Refresh
                </button>
              </div>
              <p className="text-sm text-slate-500 mb-4">Last 100 admin login attempts. After 5 failed attempts from the same IP in 15 min, the account is auto-locked for that IP.</p>
              {loginAttempts.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400">
                  Click "Load / Refresh" to view recent login attempts.
                </div>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="login-attempts-list">
                  {loginAttempts.map((a, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${a.success ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${a.success ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                        <span className="font-mono text-xs text-slate-600 truncate">{a.username}</span>
                        <span className="text-xs text-slate-400 font-mono">{a.ip}</span>
                        {a.reason === 'locked_out' && <span className="text-[10px] uppercase font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded">LOCKED</span>}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0 ml-2">
                        {new Date(a.attempted_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Database Backups */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="backups-section">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                  <Database className="w-5 h-5 text-indigo-600" />
                  Database Backups
                </h3>
                <div className="flex items-center gap-2">
                  <button onClick={fetchBackups} disabled={backupsLoading} className="btn-secondary text-sm" data-testid="refresh-backups-btn">
                    <RefreshCw className={`w-4 h-4 ${backupsLoading ? 'animate-spin' : ''}`} />
                    Load / Refresh
                  </button>
                  <button onClick={runBackupNow} disabled={backupRunning} className="btn-primary text-sm" data-testid="run-backup-now-btn">
                    <Save className={`w-4 h-4 ${backupRunning ? 'animate-pulse' : ''}`} />
                    {backupRunning ? "Backing up..." : "Backup now"}
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                A full database backup runs automatically every night at 03:00 UTC. The last 14 backups are kept on the server. Download a backup to keep your own copy in a safe place (Google Drive, Dropbox, etc).
              </p>
              <p className="text-xs text-slate-400 italic mb-4" data-testid="backup-test-marker">
                Tip: download a backup once a week and save it outside the server.
              </p>
              {backups.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400">
                  Click "Load / Refresh" to view available backups, or "Backup now" to create one immediately.
                </div>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="backups-list">
                  {backups.map((b) => (
                    <div key={b.filename} className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`backup-${b.filename}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate" title={b.filename}>{b.filename}</p>
                        <p className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString()} • {b.size_kb} KB</p>
                      </div>
                      <button
                        onClick={() => downloadBackup(b.filename)}
                        className="px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:text-white hover:bg-indigo-600 border border-indigo-200 rounded-lg transition-colors shrink-0"
                        data-testid={`download-${b.filename}`}
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      {/* Export Modal */}
      {showExportModal && exportData && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="export-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-800" style={{ fontFamily: "Nunito" }}>Export Data</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyExportData}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium"
                  data-testid="copy-export-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
                <button
                  onClick={downloadJSON}
                  className="flex items-center gap-2 px-3 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-sm font-medium"
                  data-testid="download-json-btn"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                  data-testid="close-export-btn"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto max-h-[60vh]">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-indigo-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-indigo-600">{exportData.summary?.total_pins}</p>
                  <p className="text-xs text-slate-500">Pins</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{exportData.summary?.total_areas}</p>
                  <p className="text-xs text-slate-500">Areas</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600">{exportData.summary?.total_events}</p>
                  <p className="text-xs text-slate-500">Events</p>
                </div>
                <div className="bg-pink-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-pink-600">{exportData.summary?.total_labels}</p>
                  <p className="text-xs text-slate-500">Labels</p>
                </div>
              </div>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl overflow-auto text-xs font-mono">
                {JSON.stringify(exportData, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
